import {freshStepUpProof} from './identityAccessApi';
import {authorityApi,deviceAuthorityApi} from './api';
import {runLoginCeremony} from './webauthn';
import {unlockSecurityAuthority} from './securityAuthority';

export type ProviderCatalog = {
  total:number;
  free:number;
  paid:number;
  unknown_cost:number;
  metadata_incomplete:number;
  last_sync?:{
    state:string;
    trigger:string;
    started_at:string|null;
    finished_at:string|null;
    models_discovered:number;
    models_added:number;
    models_updated:number;
    models_removed:number;
    error_code:string|null;
    source_checksum:string|null;
  }|null;
};

export type ProviderSettings = {
  provider_key:'nvidia-nim'|'openrouter';
  display_name:string;
  base_url:string;
  credential_configured:boolean;
  credential_fingerprint_prefix:string|null;
  key_version:number|null;
  created_at:string|null;
  rotated_at:string|null;
  last_verified_at:string|null;
  last_verified_status:string|null;
  credential_ref_class:string|null;
  connection_status:string;
  catalog:ProviderCatalog;
};

export type ProviderSettingsSnapshot = {
  providers:ProviderSettings[];
  secret_master_key_ready:boolean;
  notes:Record<string,unknown>;
};

export type CatalogModel = {
  provider_key:string;
  upstream_model:string;
  model_key:string|null;
  display_name:string|null;
  context_window:number|null;
  max_output_tokens:number|null;
  cost_class:'FREE'|'PAID'|'UNKNOWN';
  pricing:Record<string,unknown>;
  capabilities:string[];
  modalities:string[];
  free_declared:boolean;
  availability:string;
  catalog_state:string;
  selectable:boolean;
  last_seen_at:string|null;
  last_synced_at:string|null;
  source_checksum:string|null;
};

const BASE='/api/v1/harness/provider-settings';

async function request<T>(path:string,init?:RequestInit):Promise<T>{
  const response=await fetch(BASE+path,{
    credentials:'include',
    cache:'no-store',
    headers:{Accept:'application/json','Content-Type':'application/json',...(init?.headers??{})},
    ...init,
  });

  if(!response.ok){
    let detail=`request_failed_${response.status}`;
    try{
      const body=await response.json();
      if(typeof body?.detail==='string')detail=body.detail;
      else if(typeof body?.detail?.code==='string')detail=body.detail.code;
    }catch{}
    throw new Error(detail);
  }

  return response.json() as Promise<T>;
}

export async function unlockHarnessProviderAuthority(){
  try{
    await deviceAuthorityApi.me();
  }catch(error){
    const message=error instanceof Error?error.message:'';
    if(!/401|session|authentication|sign.in/i.test(message))throw error;
    const options=await deviceAuthorityApi.loginOptions();
    await deviceAuthorityApi.loginVerify(await runLoginCeremony(options));
  }
  await unlockSecurityAuthority();
  await authorityApi.status();
}

export const harnessProviderSettingsApi={
  snapshot:(signal?:AbortSignal)=>request<ProviderSettingsSnapshot>('',{signal}),
  models:(provider?:string,signal?:AbortSignal)=>{
    const query=provider?`?provider_key=${encodeURIComponent(provider)}`:'';
    return request<{models:CatalogModel[]}>(`/models${query}`,{signal});
  },
  async saveKey(providerKey:string,apiKey:string,reason:string){
    const proof=await freshStepUpProof();
    return request<{updated:boolean;result:Record<string,unknown>}>('/key/save/verify',{
      method:'POST',
      body:JSON.stringify({provider_key:providerKey,api_key:apiKey,reason,...proof}),
    });
  },
  async removeKey(providerKey:string,reason:string){
    const proof=await freshStepUpProof();
    return request<{updated:boolean;result:Record<string,unknown>}>('/key/remove/verify',{
      method:'POST',
      body:JSON.stringify({provider_key:providerKey,reason,...proof}),
    });
  },
  async test(providerKey:string){
    const proof=await freshStepUpProof();
    return request<{updated:boolean;result:Record<string,unknown>}>('/test/verify',{
      method:'POST',
      body:JSON.stringify({provider_key:providerKey,...proof}),
    });
  },
  async sync(providerKey:string){
    const proof=await freshStepUpProof();
    return request<{updated:boolean;result:Record<string,unknown>}>('/sync/verify',{
      method:'POST',
      body:JSON.stringify({provider_key:providerKey,...proof}),
    });
  },
};

export type ProviderMutation=()=>Promise<unknown>;
